import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowBigUp,
  ArrowBigDown,
  ImagePlus,
  MessageCircle,
  Send,
  Upload,
} from "lucide-react";

import DashboardLayout from "./DashboardLayout";
import { clearAuthUser, getAuthUser } from "../utils/auth";
import { readStoredTier } from "../utils/packageTier";
import { apiFetch } from "../utils/apiClient";

const formatCount = (value = 0) => {
  if (value < 1000) return value;
  if (value < 10000) return `${(value / 1000).toFixed(1)}k`;
  if (value < 1000000) return `${Math.round(value / 1000)}k`;
  return `${(value / 1000000).toFixed(1)}m`;
};

const defaultAvatar =
  "https://images.unsplash.com/photo-1521572267360-ee0c2909d518?q=80&w=200&auto=format&fit=crop";

const getPostTimestamp = (post) => {
  if (!post) return 0;

  const candidates = [];
  if (post.type === "share" && post.sharedAt) {
    candidates.push(post.sharedAt);
  }
  if (post.createdAt) {
    candidates.push(post.createdAt);
  }
  if (post.originalCreatedAt) {
    candidates.push(post.originalCreatedAt);
  }
  if (post.updatedAt) {
    candidates.push(post.updatedAt);
  }

  for (const value of candidates) {
    const time = new Date(value).getTime();
    if (!Number.isNaN(time)) {
      return time;
    }
  }

  return 0;
};

const mergePostIntoFeed = (feed, updatedPost) => {
  if (!updatedPost?.id) {
    return feed;
  }

  return feed.map((item) => {
    const targetId = item.postId || item.id;
    if (targetId !== updatedPost.id) {
      return item;
    }

    if (item.type === "share") {
      return {
        ...item,
        ...updatedPost,
        id: item.id,
        type: "share",
        postId: updatedPost.id,
        createdAt: item.createdAt,
        sharedAt: item.sharedAt,
        sharedBy: item.sharedBy,
        originalCreatedAt: item.originalCreatedAt ?? updatedPost.createdAt,
      };
    }

    return {
      ...item,
      ...updatedPost,
      id: updatedPost.id,
      postId: updatedPost.id,
      type: updatedPost.type || item.type || "post",
    };
  });
};

const resolveUserTier = (user) => {
  const userId = user?.id;
  const tierFromUser = Number(user?.packageTier);
  const storedTier = readStoredTier(userId);
  const tier = Number.isFinite(tierFromUser) ? tierFromUser : storedTier ?? 0;
  return Number.isFinite(tier) ? tier : 0;
};

export default function Community() {
  const navigate = useNavigate();
  const user = useMemo(() => getAuthUser(), []);
  const [posts, setPosts] = useState([]);
  const [composerMode, setComposerMode] = useState("ask");
  const [composerText, setComposerText] = useState("");
  const [composerImage, setComposerImage] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [activeReplyTargets, setActiveReplyTargets] = useState({});
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const [editingImage, setEditingImage] = useState(null);
  const [editingImageUrl, setEditingImageUrl] = useState("");
  const [editingSubmitting, setEditingSubmitting] = useState(false);
  const [shareMenuPostId, setShareMenuPostId] = useState(null);
  const [filter, setFilter] = useState("Most recent");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [showCommunityModal, setShowCommunityModal] = useState(
    () => resolveUserTier(getAuthUser()) < 2,
  );

  const handleSignOut = () => {
    clearAuthUser();
    navigate("/login", { replace: true });
  };

  // Gate: require package tier >= 2 (e.g., $200 or $500) to unlock Community
  useEffect(() => {
    const tier = resolveUserTier(user);
    setShowCommunityModal(tier < 2);
  }, [user, user?.id, user?.packageTier]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (showCommunityModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showCommunityModal]);

  useEffect(
    () => () => {
      if (composerImage?.objectUrl) {
        URL.revokeObjectURL(composerImage.objectUrl);
      }
    },
    [composerImage],
  );

  const fetchPosts = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch("/community", {
        headers: { "x-user-id": user.id },
      });
      setPosts(data?.posts || []);
    } catch (fetchError) {
      setError(fetchError.message || "Failed to load community posts.");
      setFeedback("");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    if (composerImage?.objectUrl) {
      URL.revokeObjectURL(composerImage.objectUrl);
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        setComposerImage({ objectUrl, dataUrl: reader.result });
      }
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const handleRemoveImage = () => {
    if (composerImage?.objectUrl) {
      URL.revokeObjectURL(composerImage.objectUrl);
    }
    setComposerImage(null);
  };

  const handleCreatePost = async (event) => {
    event.preventDefault();
    const trimmed = composerText.trim();
    if ((!trimmed && !composerImage) || !user?.id) return;

    setSubmitting(true);
    setError("");
    setFeedback("");

    try {
      const payload = { content: trimmed };
      if (composerImage?.dataUrl) {
        payload.imageUrl = composerImage.dataUrl;
      }

      const response = await apiFetch("/community", {
        method: "POST",
        headers: { "x-user-id": user.id },
        data: payload,
      });

      setPosts((previous) => [response.post, ...previous]);
      setComposerText("");
      setComposerMode("ask");
      if (composerImage?.objectUrl) {
        URL.revokeObjectURL(composerImage.objectUrl);
      }
      setComposerImage(null);
    } catch (createError) {
      setError(createError.message || "Failed to share your update.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLike = async (postId) => {
    if (!user?.id) return;
    try {
      const response = await apiFetch(`/community/${postId}/like`, {
        method: "POST",
        headers: { "x-user-id": user.id },
      });
      setPosts((previous) => mergePostIntoFeed(previous, response.post));
    } catch (likeError) {
      setError(likeError.message || "Failed to update like.");
      setFeedback("");
    }
  };

  const toggleDislike = async (postId) => {
    if (!user?.id) return;
    try {
      const response = await apiFetch(`/community/${postId}/dislike`, {
        method: "POST",
        headers: { "x-user-id": user.id },
      });
      setPosts((previous) => mergePostIntoFeed(previous, response.post));
    } catch (dislikeError) {
      setError(dislikeError.message || "Failed to update dislike.");
      setFeedback("");
    }
  };

  const updateFollowStateInFeed = useCallback((authorId, following, followersCount) => {
    setPosts((previous) =>
      previous.map((post) => {
        if (post.author?.id !== authorId) {
          return post;
        }
        return {
          ...post,
          author: post.author
            ? {
                ...post.author,
                followedByMe: following,
                followersCount:
                  typeof followersCount === "number"
                    ? followersCount
                    : post.author.followersCount,
              }
            : post.author,
        };
      }),
    );
  }, []);

  const handleToggleFollow = async (authorId, currentlyFollowing) => {
    if (!user?.id || !authorId || authorId === user.id) return;
    try {
      const response = await apiFetch(
        currentlyFollowing
          ? `/community/users/${authorId}/unfollow`
          : `/community/users/${authorId}/follow`,
        {
          method: "POST",
          headers: { "x-user-id": user.id },
        },
      );
      updateFollowStateInFeed(
        response?.userId || authorId,
        Boolean(response?.following),
        response?.followersCount,
      );
      setFeedback(
        response?.message ||
          (response?.following ? "You are now following this user." : "Unfollowed user."),
      );
      setError("");
    } catch (followError) {
      setError(followError.message || "Failed to update follow state.");
      setFeedback("");
    }
  };

  const handleCommentChange = (postId, value) => {
    setCommentInputs((previous) => ({ ...previous, [postId]: value }));
    if (!value.trim()) {
      setActiveReplyTargets((previous) => {
        if (!previous[postId]) {
          return previous;
        }
        const next = { ...previous };
        delete next[postId];
        return next;
      });
    }
  };

  const handleAddComment = async (postId) => {
    const value = commentInputs[postId]?.trim();
    if (!value || !user?.id) return;
    const replyTarget = activeReplyTargets[postId];

    try {
      const response = await apiFetch(`/community/${postId}/comments`, {
        method: "POST",
        headers: { "x-user-id": user.id },
        data: {
          text: value,
          parentCommentId: replyTarget?.commentId,
        },
      });
      setPosts((previous) => mergePostIntoFeed(previous, response.post));
      setCommentInputs((previous) => ({ ...previous, [postId]: "" }));
      setActiveReplyTargets((previous) => {
        if (!previous[postId]) {
          return previous;
        }
        const next = { ...previous };
        delete next[postId];
        return next;
      });
    } catch (commentError) {
      setError(commentError.message || "Failed to add comment.");
      setFeedback("");
    }
  };

  const handleStartReply = (postId, comment) => {
    if (!comment) return;
    const mentionName = comment.author?.name?.split(" ")?.[0] || "member";
    const mentionText = `@${mentionName}`;
    setActiveReplyTargets((previous) => ({
      ...previous,
      [postId]: {
        commentId: comment.id,
        name: comment.author?.name || "Community Member",
      },
    }));
    setCommentInputs((previous) => ({
      ...previous,
      [postId]: `${mentionText} `,
    }));
  };

  const handleCancelReply = (postId) => {
    setActiveReplyTargets((previous) => {
      if (!previous[postId]) {
        return previous;
      }
      const next = { ...previous };
      delete next[postId];
      return next;
    });
    setCommentInputs((previous) => ({
      ...previous,
      [postId]: "",
    }));
  };

  const handleToggleCommentLike = async (postId, commentId) => {
    if (!user?.id) return;
    try {
      const response = await apiFetch(`/community/${postId}/comments/${commentId}/like`, {
        method: "POST",
        headers: { "x-user-id": user.id },
      });
      setPosts((previous) => mergePostIntoFeed(previous, response.post));
    } catch (commentLikeError) {
      setError(commentLikeError.message || "Failed to update comment like.");
      setFeedback("");
    }
  };

  const handleToggleCommentDislike = async (postId, commentId) => {
    if (!user?.id) return;
    try {
      const response = await apiFetch(`/community/${postId}/comments/${commentId}/dislike`, {
        method: "POST",
        headers: { "x-user-id": user.id },
      });
      setPosts((previous) => mergePostIntoFeed(previous, response.post));
    } catch (commentDislikeError) {
      setError(commentDislikeError.message || "Failed to update comment dislike.");
      setFeedback("");
    }
  };

  const clearEditingImage = useCallback(() => {
    setEditingImage((previous) => {
      if (previous?.objectUrl) {
        URL.revokeObjectURL(previous.objectUrl);
      }
      return null;
    });
    setEditingImageUrl("");
  }, []);

  const resetEditingState = useCallback(() => {
    clearEditingImage();
    setEditingPostId(null);
    setEditingContent("");
    setEditingSubmitting(false);
  }, [clearEditingImage]);

  useEffect(
    () => () => {
      if (editingImage?.objectUrl) {
        URL.revokeObjectURL(editingImage.objectUrl);
      }
    },
    [editingImage],
  );

  const startEditingPost = (post) => {
    if (!post) return;
    const targetId = (post.postId || post.id)?.toString();
    if (!targetId) return;
    clearEditingImage();
    setEditingPostId(targetId);
    setEditingContent(post.content || "");
    setEditingImageUrl(post.imageUrl || "");
  };

  const handleEditImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearEditingImage();

    const objectUrl = URL.createObjectURL(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        setEditingImage({ objectUrl, dataUrl: reader.result });
        setEditingImageUrl(reader.result);
      } else {
        URL.revokeObjectURL(objectUrl);
      }
    };
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const handleRemoveEditImage = () => {
    clearEditingImage();
  };

  const handleUpdatePost = async () => {
    if (!editingPostId || !user?.id) return;
    const trimmed = editingContent.trim();
    if (!trimmed && !editingImageUrl) {
      setError("Please add some text or attach an image before saving.");
      setFeedback("");
      return;
    }

    setEditingSubmitting(true);
    try {
      const payload = {};
      if (editingContent !== "") {
        payload.content = trimmed;
      }
      payload.imageUrl = editingImage?.dataUrl ?? editingImageUrl ?? "";

      const response = await apiFetch(`/community/${editingPostId}`, {
        method: "PATCH",
        headers: { "x-user-id": user.id },
        data: payload,
      });

      setPosts((previous) => mergePostIntoFeed(previous, response.post));
      resetEditingState();
      setFeedback(response?.message || "Post updated.");
      setError("");
    } catch (updateError) {
      setError(updateError.message || "Failed to update post.");
      setFeedback("");
    } finally {
      setEditingSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!user?.id) return;
    const confirmed =
      typeof window === "undefined" ||
      window.confirm("Are you sure you want to delete this post?");
    if (!confirmed) return;

    try {
      const response = await apiFetch(`/community/${postId}`, {
        method: "DELETE",
        headers: { "x-user-id": user.id },
      });

      const removedId = response?.postId || postId;
      setPosts((previous) =>
        previous.filter((post) => {
          const targetId = (post.postId || post.id)?.toString();
          return targetId !== removedId;
        }),
      );

      if (editingPostId === removedId) {
        resetEditingState();
      }

      setFeedback(response?.message || "Post deleted.");
      setError("");
    } catch (deleteError) {
      setError(deleteError.message || "Failed to delete post.");
      setFeedback("");
    }
  };

  const handleSharePost = async (postId) => {
    if (!user?.id) return;

    try {
      const response = await apiFetch(`/community/${postId}/share`, {
        method: "POST",
        headers: { "x-user-id": user.id },
      });

      if (response?.sharedFeedItem) {
        setPosts((previous) => {
          const withoutExisting = previous.filter(
            (post) => post.id !== response.sharedFeedItem.id,
          );
          return [response.sharedFeedItem, ...withoutExisting];
        });
      }

      setFeedback(response?.message || "Post shared to the community feed.");
      setError("");
    } catch (shareError) {
      setError(shareError.message || "Failed to share post.");
      setFeedback("");
    }
  };

  const buildShareUrl = (postId) => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/community?post=${postId}`;
  };

  const handleShareToWhatsApp = (post) => {
    const targetPostId = (post?.postId || post?.id)?.toString();
    if (!targetPostId) {
      setError("Unable to share this post at the moment.");
      setFeedback("");
      setShareMenuPostId(null);
      return;
    }

    const shareUrl = buildShareUrl(targetPostId);
    const textSnippet = post?.content
      ? post.content.length > 140
        ? `${post.content.slice(0, 137)}...`
        : post.content
      : "Check out this SynergyCoin community update!";

    const message = `${textSnippet}\n${shareUrl}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;

    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        navigator
          .share({
            text: textSnippet,
            url: shareUrl,
            title: "SynergyCoin Community Update",
          })
          .catch(() => {
            window.open(whatsappUrl, "_blank", "noopener,noreferrer");
          });
      } else if (typeof window !== "undefined") {
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      }
      setFeedback("Opening WhatsApp to share your update.");
      setError("");
    } catch (shareError) {
      setError("WhatsApp sharing failed. Please try copying the link instead.");
      setFeedback("");
    } finally {
      setShareMenuPostId(null);
    }
  };

  const handleCopyShareLink = async (postId) => {
    const shareUrl = buildShareUrl(postId);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.createElement("input");
        input.value = shareUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setFeedback("Post link copied to clipboard.");
      setError("");
    } catch (copyError) {
      setError("Could not copy the link. Please try manually.");
      setFeedback("");
    } finally {
      setShareMenuPostId(null);
    }
  };

  const toggleShareMenu = (postId) => {
    setShareMenuPostId((previous) => (previous === postId ? null : postId));
  };

  const sortedPosts = useMemo(() => {
    const copy = [...posts];
    const compareByNewest = (a, b) => getPostTimestamp(b) - getPostTimestamp(a);

    if (filter === "Most recent") {
      return copy.sort(compareByNewest);
    }
    if (filter === "Least recent") {
      return copy.sort((a, b) => getPostTimestamp(a) - getPostTimestamp(b));
    }
    return copy.sort((a, b) => {
      const likeDifference = (b.likes || 0) - (a.likes || 0);
      if (likeDifference !== 0) {
        return likeDifference;
      }
      return compareByNewest(a, b);
    });
  }, [posts, filter]);

  return (
    <DashboardLayout user={user} onSignOut={handleSignOut} activeKey="community">
      {({ displayName: layoutName }) => (
        <div className="relative">
          <header
            className={`relative ${
              showCommunityModal ? "z-[60] pointer-events-none" : "z-10"
            }`}
          >
            <div className="mb-6 text-sm text-white/50">
              Dashboard <span className="mx-2 text-white/30">›</span> Community
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Community
            </h1>
            <p className="mt-2 text-sm text-white/60">
              Stay updated with real-time alerts, share insights, and join the conversation.
            </p>
          </header>

          {/* Community Member Modal Card - Mobile only, below "Stay updated" text */}
          {showCommunityModal && (
            <div className="mt-6 mb-6 md:hidden">
              <div className="relative w-full max-w-2xl mx-auto rounded-[20px] sm:rounded-[28px] bg-gradient-to-br from-[#1A0F2E] via-[#0F0A1A] to-[#050308] p-5 sm:p-6 md:p-7 shadow-[0_32px_90px_rgba(10,10,30,0.65)] border border-white/5">
                <div className="flex flex-col items-center text-center">
                  {/* Mobile Image */}
                  <div className="mb-3 sm:mb-4 md:mb-5">
                    <img 
                      src="/images/mobile.png" 
                      alt="Community Mobile" 
                      className="h-20 w-auto sm:h-28 md:h-32"
                    />
                  </div>
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight">
                    Become a Community member
                  </h2>
                  <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/70">
                    Minimum 200 USD required
                  </p>
                  <p className="mt-1 text-base sm:text-lg md:text-xl font-bold text-[#A855F7]">
                    (400,000 coins)
                  </p>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6 space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
                  <p className="font-bold text-white text-sm sm:text-base">Community member Incentives</p>
                  <ul className="list-disc space-y-1.5 sm:space-y-2 pl-4 sm:pl-5 md:pl-6 text-left text-white/80">
                    <li className="text-xs sm:text-sm leading-relaxed">Receive community affiliate unique code</li>
                    <li className="text-xs sm:text-sm leading-relaxed">AffiliateReferral link and QR code</li>
                    <li className="text-xs sm:text-sm leading-relaxed">
                      Receive coins bonus for community purchases under their unique code
                      (each coin note purchase is a minimum $25)
                    </li>
                    <li className="text-xs sm:text-sm leading-relaxed">Receive extra 5k coin bonus per coin note purchase.</li>
                  </ul>
                </div>
                <div className="mt-4 sm:mt-5 md:mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCommunityModal(false);
                      navigate("/packages");
                    }}
                    className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-[#8C3CDD] to-[#A367FF] py-3 sm:py-3.5 md:py-4 text-sm sm:text-base font-bold text-white shadow-[0_8px_24px_rgba(140,60,221,0.4)] transition hover:shadow-[0_12px_32px_rgba(140,60,221,0.6)] hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Buy Coins
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`mt-6 space-y-8 ${showCommunityModal ? "blur-[2px] opacity-90 pointer-events-none" : ""}`}>
            {error && (
              <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            )}

            {feedback && !error && (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                {feedback}
              </div>
            )}

          <section className="rounded-3xl border border-[#1E2228] bg-[#101315] p-6 shadow-[0_24px_80px_rgba(21,8,45,0.45)] backdrop-blur">
            <form onSubmit={handleCreatePost} className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#6E3FCB] to-[#9F53FF] text-center text-lg font-semibold leading-[48px] text-white">
                  {layoutName.charAt(0).toUpperCase()}
                </div>
                <input
                  type="text"
                  value={composerText}
                  onChange={(event) => setComposerText(event.target.value)}
                  placeholder="What do you want to ask or share?"
                  className="flex-1 rounded-full border border-[#1E2228] bg-[#101315] px-5 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/40"
                />
              </div>
          
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-full border border-[#1E2228] bg-[#101315] px-4 py-2 text-sm text-white/70">
                <div className="flex flex-1 items-center gap-3">
                  {["ask", "answer", "post"].map((mode) => (
                    <button
                      type="button"
                      key={mode}
                      onClick={() => setComposerMode(mode)}
                      className={`rounded-full px-4 py-2 transition ${
                        composerMode === mode
                          ? "bg-[#8C3CDD] text-white"
                          : "hover:bg-white/5"
                      }`}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex cursor-pointer items-center gap-2 rounded-full border border-[#1E2228] bg-[#101315] px-4 py-2 text-sm text-white transition hover:bg-white/10">
                    <ImagePlus size={16} />
                    Add media
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-full bg-[#8C3CDD] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#A367FF] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Send size={16} />
                    {submitting ? "Sharing..." : "Share"}
                  </button>
                </div>
              </div>

              {composerImage && (
                <div className="relative rounded-2xl border border-[#1E2228] bg-[#101315]">
                  <div className="max-h-[400px] overflow-hidden">
                    <img
                      src={composerImage.dataUrl || composerImage.objectUrl}
                      alt="Upload preview"
                      className="mx-auto max-h-[400px] w-full rounded-2xl object-cover object-center"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-2 text-xs font-semibold text-white transition hover:bg-black/90"
                  >
                    Remove
                  </button>
                </div>
              )}
            </form>
            
          </section>

          <section className="space-y-6 pb-24 sm:pb-8 md:pb-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-semibold text-white">Feed</h2>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowFilterMenu((previous) => !previous)}
                  className="inline-flex items-center gap-2 rounded-full border border-[#1E2228] bg-[#101315] px-4 py-2 text-sm text-white/80 transition hover:bg-white/10"
                >
                  Recommend
                  <span className="text-white/40">{filter}</span>
                </button>
                {showFilterMenu && (
                  <div className="absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-xl border border-white/10 bg-black/90 text-sm text-white/80 shadow-lg">
                    {["Recommended", "Most recent", "Least recent"].map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setFilter(option);
                          setShowFilterMenu(false);
                        }}
                        className={`block w-full px-4 py-2 text-left transition hover:bg-white/10 ${
                          option === filter ? "text-white" : ""
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="rounded-3xl border border-[#1E2228] bg-[#101315] p-10 text-center text-sm text-white/60">
                Loading community feed…
              </div>
            ) : (
              <div className="space-y-6">
                {sortedPosts.map((post) => {
                  const targetPostId = (post.postId || post.id)?.toString();
                  const originalTimestamp = post.originalCreatedAt || post.createdAt;
                  const isMyPost = Boolean(post.author?.id && user?.id && post.author.id === user.id);
                  const isShare = post.type === "share";
                  const commentList = Array.isArray(post.comments) ? post.comments : [];
                  const commentMap = new Map();
                  commentList.forEach((comment) => {
                    commentMap.set(comment.id, { ...comment, replies: [] });
                  });
                  commentList.forEach((comment) => {
                    const entry = commentMap.get(comment.id);
                    if (comment.replyTo && commentMap.has(comment.replyTo)) {
                      const parent = commentMap.get(comment.replyTo);
                      entry.replyToName = parent?.author?.name || "original comment";
                      parent.replies.push(entry);
                    }
                  });
                  const topLevelComments = [];
                  commentList.forEach((comment) => {
                    if (!comment.replyTo || !commentMap.has(comment.replyTo)) {
                      topLevelComments.push(commentMap.get(comment.id));
                    }
                  });
                  const renderCommentThread = (commentEntry, depth = 0) => (
                    <div
                      key={commentEntry.id}
                      className={`${depth > 0 ? "pl-4 sm:pl-6 border-l border-[#1E2228]" : ""} space-y-4`}
                    >
                      <div
                        className={`rounded-2xl border border-[#1E2228] ${
                          depth === 0 ? "bg-[#101315]" : "bg-[#0C1011]"
                        } p-4 text-sm text-white/80`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={commentEntry.author?.avatarUrl || defaultAvatar}
                              alt={commentEntry.author?.name || "Member"}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                            <span className="font-semibold text-white">
                              {commentEntry.author?.name || "Community Member"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleCommentLike(targetPostId, commentEntry.id)
                              }
                              className={`inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-xs transition ${
                                commentEntry.likedByMe
                                  ? "bg-[#8C3CDD] text-white"
                                  : "text-white/60 hover:bg-white/5"
                              }`}
                            >
                              <ArrowBigUp size={12} />
                              {formatCount(commentEntry.likes)}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleCommentDislike(targetPostId, commentEntry.id)
                              }
                              className={`inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-xs transition ${
                                commentEntry.dislikedByMe
                                  ? "bg-[#EF4444] text-white"
                                  : "text-white/60 hover:bg-white/5"
                              }`}
                            >
                              <ArrowBigDown size={12} />
                              {formatCount(commentEntry.dislikes)}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStartReply(targetPostId, commentEntry)}
                              className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-xs text-white/60 transition hover:bg-white/5"
                            >
                              Reply
                            </button>
                          </div>
                        </div>
                        {commentEntry.replyToName && (
                          <p className="mt-2 text-xs text-white/50">
                            Replying to{" "}
                            <span className="font-semibold text-white">
                              {commentEntry.replyToName}
                            </span>
                          </p>
                        )}
                        <p className="mt-3 text-sm leading-6 text-white/70">
                          {commentEntry.text}
                        </p>
                      </div>
                      {commentEntry.replies.length > 0 && (
                        <div className="space-y-4">
                          {commentEntry.replies.map((reply) =>
                            renderCommentThread(reply, depth + 1),
                          )}
                        </div>
                      )}
                    </div>
                  );

                  const engagementSection = (
                    <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/60">
                      <button
                        type="button"
                        onClick={() => toggleLike(targetPostId)}
                        className={`inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 transition ${
                          post.likedByMe ? "bg-[#8C3CDD] text-white" : "hover:bg-white/5"
                        }`}
                      >
                        <ArrowBigUp size={16} />
                        Up Vote {formatCount(post.likes)}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleDislike(targetPostId)}
                        className={`inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 transition ${
                          post.dislikedByMe ? "bg-[#EF4444] text-white" : "hover:bg-white/5"
                        }`}
                      >
                        <ArrowBigDown size={16} />
                        Down Vote {formatCount(post.dislikes)}
                      </button>
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2">
                        <MessageCircle size={16} />
                        Comment {post.comments?.length || 0}
                      </span>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => toggleShareMenu(targetPostId)}
                          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 transition hover:bg-white/5"
                        >
                          <Upload size={16} />
                          Share
                        </button>
                        {shareMenuPostId === targetPostId && (
                          <div className="absolute right-0 z-30 mt-2 w-56 rounded-2xl border border-white/10 bg-black/90 text-xs text-white/80 shadow-lg">
                            <button
                              type="button"
                              onClick={() => {
                                handleSharePost(targetPostId).finally(() =>
                                  setShareMenuPostId(null),
                                );
                              }}
                              className="block w-full px-4 py-2 text-left transition hover:bg-white/10"
                            >
                              Feature on my profile
                            </button>
                            <button
                              type="button"
                              onClick={() => handleShareToWhatsApp(post)}
                              className="block w-full px-4 py-2 text-left transition hover:bg-white/10"
                            >
                              Share via WhatsApp
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCopyShareLink(targetPostId)}
                              className="block w-full px-4 py-2 text-left transition hover:bg-white/10"
                            >
                              Copy share link
                            </button>
                          </div>
                        )}
                      </div>
                      
                    </div>
                    
                  );

                  const commentSection = (
                    <div className="mt-6 space-y-4 rounded-2xl border border-[#1E2228] bg-[#101315] p-4">
                      <h3 className="text-sm font-semibold text-white/80">Comment</h3>
                      {activeReplyTargets[targetPostId] && (
                        <div className="flex items-center justify-between rounded-full border border-[#8C3CDD]/40 bg-[#8C3CDD]/10 px-4 py-2 text-xs text-white/80">
                          <span>
                            Replying to{" "}
                            <span className="font-semibold text-white">
                              {activeReplyTargets[targetPostId].name}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCancelReply(targetPostId)}
                            className="text-white/60 transition hover:text-white"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-3 rounded-full border border-[#1E2228] bg-[#101315] px-4 py-3">
                        <input
                          type="text"
                          placeholder="What do you want to ask or share?"
                          value={commentInputs[targetPostId] || ""}
                          onChange={(event) =>
                            handleCommentChange(targetPostId, event.target.value)
                          }
                          className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddComment(targetPostId)}
                          className="inline-flex items-center gap-2 rounded-full bg-[#8C3CDD] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#A367FF]"
                        >
                          <Send size={14} />
                          Reply
                        </button>
                      </div>

                      <div className="space-y-4">
                        {topLevelComments.length > 0 ? (
                          topLevelComments.map((commentEntry) =>
                            renderCommentThread(commentEntry),
                          )
                        ) : (
                          <p className="text-xs text-white/40">
                            No comments yet. Start the discussion!
                          </p>
                        )}
                      </div>
                    </div>
                  );

                  if (isShare) {
                    const sharedMeta = post.sharedBy || {};
                    const sharedTimestamp = post.sharedAt
                      ? new Date(post.sharedAt).toLocaleString()
                      : "Just now";

                    return (
                      <article
                        key={post.id}
                        className="w-full overflow-hidden rounded-3xl border border-[#1E2228] bg-[#101315] shadow-[0_28px_90px_rgba(8,2,25,0.55)] backdrop-blur-xl"
                      >
                        <div className="space-y-6 p-6">
                          <div className="flex items-start gap-3">
                            <img
                              src={sharedMeta.avatarUrl || defaultAvatar}
                              alt={sharedMeta.name || "Community Member"}
                              className="h-11 w-11 rounded-full object-cover"
                            />
                            <div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-semibold text-white">
                                  {sharedMeta.name || "Community Member"}
                                </span>
                                <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-white/60">
                                  {sharedMeta.role || "Member"}
                                </span>
                              </div>
                              <p className="text-xs text-white/50">Shared {sharedTimestamp}</p>
                            </div>
                          </div>

                          {post.shareMessage && (
                            <p className="text-sm leading-6 text-white/70">
                              {post.shareMessage}
                            </p>
                          )}

                          <div className="rounded-2xl border border-[#1E2228] bg-[#0C1011] p-5">
                            <header className="flex items-start gap-3">
                              <img
                                src={post.author?.avatarUrl || defaultAvatar}
                                alt={post.author?.name || "Community member"}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                              <div>
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="font-semibold text-white">
                                    {post.author?.name || "Synergy Member"}
                                  </span>
                                  <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-white/60">
                                    {post.author?.role || "Community"}
                                  </span>
                                </div>
                                <p className="text-xs text-white/50">
                                  {originalTimestamp
                                    ? new Date(originalTimestamp).toLocaleString()
                                    : "Just now"}
                                </p>
                              </div>
                            </header>

                            {post.content && (
                              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-white/70">
                                {post.content}
                              </p>
                            )}

                            {post.imageUrl && (
                              <div className="mt-4 overflow-hidden rounded-2xl border border-[#1E2228]">
                                <img
                                  src={post.imageUrl}
                                  alt=""
                                  className="mx-auto w-full object-cover object-center"
                                />
                              </div>
                            )}
                          </div>

                          {engagementSection}
                          {commentSection}
                        </div>
                      </article>
                    );
                  }

                  return (
                    <article
                      key={post.id}
                      className="w-full overflow-hidden rounded-3xl border border-[#1E2228] bg-[#101315] shadow-[0_28px_90px_rgba(8,2,25,0.55)] backdrop-blur-xl"
                    >
                      <div className="space-y-6 p-6">
                      {post.type === "share" && post.sharedBy && (
                        <div className="rounded-2xl border border-[#1E2228] bg-[#0C1011] p-4">
                          <div className="flex items-center gap-3 pb-4">
                            <img
                              src={post.sharedBy.avatarUrl || defaultAvatar}
                              alt={post.sharedBy.name || "Community Member"}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="text-sm font-semibold text-white">
                                {post.sharedBy.name || "Community Member"}
                              </p>
                              <p className="text-xs text-white/60">
                                Shared {post.sharedAt ? new Date(post.sharedAt).toLocaleString() : "recently"}
                              </p>
                            </div>
                          </div>
                          <div className="rounded-2xl border border-[#1E2228] bg-[#101315] p-4">
                            <header className="flex items-center gap-3 pb-4">
                              <img
                                src={post.author?.avatarUrl || defaultAvatar}
                                alt={post.author?.name || "Community member"}
                                className="h-9 w-9 rounded-full object-cover"
                              />
                              <div>
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="font-semibold text-white">
                                    {post.author?.name || "Synergy Member"}
                                  </span>
                                  <span className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-white/60">
                                    {post.author?.role || "Community"}
                                  </span>
                                </div>
                                <p className="text-xs text-white/50">
                                  {originalTimestamp
                                    ? new Date(originalTimestamp).toLocaleString()
                                    : "Just now"}
                                </p>
                              </div>
                            </header>
                            {post.content && (
                              <p className="whitespace-pre-line text-sm leading-6 text-white/75">
                                {post.content}
                              </p>
                            )}
                            {post.imageUrl && (
                              <div className="mt-4 flex justify-center">
                                <img
                                  src={post.imageUrl}
                                  alt=""
                                  className="h-[434px] w-[1092px] max-w-full rounded-2xl border border-[#1E2228] object-cover"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <header className="flex items-start gap-3">
                        <img
                          src={post.author?.avatarUrl || defaultAvatar}
                          alt={post.author?.name || "Community member"}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                        <div className="flex flex-1 items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="font-semibold text-white">
                                {post.author?.name || "Synergy Member"}
                              </span>
                              <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/60">
                                {post.author?.role || "Community"}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-white/60">
                              {originalTimestamp
                                ? new Date(originalTimestamp).toLocaleString()
                                : "Just now"}
                              {post.type === "share" && post.sharedAt && (
                                <span className="ml-2 text-white/40">
                                  Shared {new Date(post.sharedAt).toLocaleString()}
                                </span>
                              )}
                            </p>
                            {typeof post.author?.followersCount === "number" && (
                              <p className="text-xs text-white/40">
                                {post.author.followersCount} follower
                                {post.author.followersCount === 1 ? "" : "s"}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isMyPost && !isShare ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEditingPost(post)}
                                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                                    editingPostId === targetPostId
                                      ? "border-[#8C3CDD]/60 bg-[#8C3CDD]/20 text-[#E3D4FF]"
                                      : "border-white/15 bg-white/5 text-white hover:bg-white/10"
                                  }`}
                                >
                                  {editingPostId === targetPostId ? "Editing…" : "Edit"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePost(targetPostId)}
                                  className="rounded-full border border-red-400/40 bg-red-500/10 px-4 py-1.5 text-xs font-semibold text-red-300 transition hover:bg-red-500/15"
                                >
                                  Delete
                                </button>
                              </>
                            ) : (
                              post.author?.id &&
                              post.author.id !== user?.id && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleToggleFollow(
                                      post.author.id,
                                      Boolean(post.author?.followedByMe),
                                    )
                                  }
                                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                                    post.author?.followedByMe
                                      ? "border-white/20 bg-white/10 text-white hover:bg-white/15"
                                      : "border-[#8C3CDD]/60 bg-[#8C3CDD]/20 text-[#D8B4FE] hover:bg-[#8C3CDD]/30"
                                  }`}
                                >
                                  {post.author?.followedByMe ? "Following" : "Follow"}
                                </button>
                              )
                            )}
                          </div>
                        </div>
                      </header>

                      {editingPostId === targetPostId ? (
                        <div className="mt-4 space-y-4 rounded-2xl border border-[#1E2228] bg-[#101315] p-4">
                          <textarea
                            value={editingContent}
                            onChange={(event) => setEditingContent(event.target.value)}
                            placeholder="Update your post content"
                            className="min-h-[120px] w-full rounded-xl border border-[#1E2228] bg-[#101315] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#8C3CDD] focus:outline-none focus:ring-2 focus:ring-[#8C3CDD]/40"
                          />
                          <div className="flex flex-wrap items-center gap-3 text-xs text-white/70">
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#1E2228] bg-[#101315] px-4 py-2 transition hover:bg-white/10">
                              <ImagePlus size={14} />
                              Change media
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={handleEditImageUpload}
                              />
                            </label>
                            {(editingImage || editingImageUrl) && (
                              <button
                                type="button"
                                onClick={handleRemoveEditImage}
                                className="inline-flex items-center gap-2 rounded-full border border-[#1E2228] bg-[#101315] px-4 py-2 text-white transition hover:bg-white/10"
                              >
                                Remove image
                              </button>
                            )}
                          </div>
                          {(editingImage || editingImageUrl) && (
                            <div
                              className="overflow-hidden rounded-2xl border border-[#1E2228]"
                              style={{ aspectRatio: "16 / 9" }}
                            >
                              <img
                                src={editingImage?.dataUrl || editingImageUrl}
                                alt="Editing preview"
                                className="h-full w-full object-cover object-center"
                              />
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={handleUpdatePost}
                              disabled={editingSubmitting}
                              className="inline-flex items-center gap-2 rounded-full bg-[#8C3CDD] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#A367FF] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {editingSubmitting ? "Saving…" : "Save changes"}
                            </button>
                            <button
                              type="button"
                              onClick={resetEditingState}
                              className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {post.content && (
                            <p className="mt-3 whitespace-pre-line text-sm leading-6 text-white/70">
                              {post.content}
                            </p>
                          )}
                          {post.imageUrl && (
                            <div className="mt-4 flex justify-center">
                              <img
                                src={post.imageUrl}
                                alt=""
                                className="h-[434px] w-[1092px] max-w-full rounded-2xl border border-white/10 object-cover"
                              />
                            </div>
                          )}
                        </>
                      )}

                      {engagementSection}
                      {commentSection}
                    </div>
                    </article>
                  );
                })}

                {!sortedPosts.length && !loading && (
                  <div className="rounded-3xl border border-dashed border-[#1E2228] bg-[#101315] p-10 text-center text-sm text-white/60">
                    No posts yet. Create the first conversation in the community!
                  </div>
                )}
              </div>
            )}
          </section>
          </div>

          {/* Community Member Modal - Desktop only */}
          {showCommunityModal && (
 <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center bg-black/30 backdrop-blur-sm px-4 pt-6 pb-6 sm:py-8 md:pt-24 md:left-[280px] overflow-y-auto">
<div className="relative w-full max-w-lg rounded-[20px] sm:rounded-[28px] bg-gradient-to-br from-[#1A0F2E] via-[#0F0A1A] to-[#050308] p-5 sm:p-6 md:p-7 shadow-[0_32px_90px_rgba(10,10,30,0.65)] border border-white/5">
            <div className="flex flex-col items-center text-center">
              {/* Mobile Image */}
              <div className="mb-3 sm:mb-4 md:mb-5">
                <img 
                  src="/images/mobile.png" 
                  alt="Community Mobile" 
                  className="h-20 w-auto sm:h-28 md:h-32"
                />
              </div>
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight">
                Become a Community member
              </h2>
                <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/70">
                Minimum 200 USD required
                </p>
                <p className="mt-1 text-base sm:text-lg md:text-xl font-bold text-[#A855F7]">
                (400,000 coins)
                </p>
            </div>
            <div className="mt-4 sm:mt-5 md:mt-6 space-y-2 sm:space-y-3 text-xs sm:text-sm text-white/80">
              <p className="font-bold text-white text-sm sm:text-base">Community member Incentives</p>
              <ul className="list-disc space-y-1.5 sm:space-y-2 pl-4 sm:pl-5 md:pl-6 text-left text-white/80">
                <li className="text-xs sm:text-sm leading-relaxed">Receive community affiliate unique code</li>
                <li className="text-xs sm:text-sm leading-relaxed">AffiliateReferral link and QR code</li>
                  <li className="text-xs sm:text-sm leading-relaxed">
                  Receive coins bonus for community purchases under their unique code
                  (each coin note purchase is a minimum $25)
                  </li>
                <li className="text-xs sm:text-sm leading-relaxed">Receive extra 5k coin bonus per coin note purchase.</li>
              </ul>
            </div>
            <div className="mt-4 sm:mt-5 md:mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowCommunityModal(false);
                  navigate("/packages");
                }}
                className="w-full rounded-lg sm:rounded-xl bg-gradient-to-r from-[#8C3CDD] to-[#A367FF] py-3 sm:py-3.5 md:py-4 text-sm sm:text-base font-bold text-white shadow-[0_8px_24px_rgba(140,60,221,0.4)] transition hover:shadow-[0_12px_32px_rgba(140,60,221,0.6)] hover:scale-[1.02] active:scale-[0.98]"
              >
                Buy Coins
              </button>
            </div>
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
